/**
 * Detection / Rules - Phase 4 wiring.
 *
 * Live data source: GET /v1/detection/rules
 * Each row is clickable to inspect the Rego source; the toolbar "New
 * rule" opens a small modal that calls POST /rules (draft) followed
 * by an optional dry-run + publish.
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { ScrollText, Plus, Eye, Play, CheckCircle2, AlertTriangle, Loader2, X } from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import {
  listDetectionRules,
  createDetectionRule,
  dryRunDetectionRule,
  publishDetectionRule,
  patchDetectionRuleStatus,
  evaluateDetectionRule,
  runDetectionEnforcement,
  postSyntheticError,
  type DetectionRule,
  type EvaluateRuleInput,
  type EvaluateRuleResult,
  type RunEnforcementInput,
  type RunEnforcementResult,
  type SyntheticErrorResult,
  type DbVendor,
} from '@/lib/api';
import { Zap, FlaskConical, Bug } from 'lucide-react';

// Sample payloads for the testers. Both are valid against the live Zod
// schemas (detection/schemas.ts) so the panels work out-of-the-box.
const EVAL_SAMPLE: EvaluateRuleInput = {
  event: {
    actorRole: 'security_operator',
    statementType: 'SELECT',
    tablesTouched: ['aadhaar_vault'],
    flags: ['off_hours_access'],
  },
  context: {
    sourceCountryCode: 'IN',
    purposeDeclared: 'fraud_investigation',
  },
};

const ENFORCE_SAMPLE: RunEnforcementInput = {
  vendor: 'postgres',
  tablesTouched: [{ name: 'aadhaar_vault', holdsMinorData: false }],
  piiCategoriesTouched: ['aadhaar'],
  actorRole: 'security_operator',
  actorPermissions: ['detection:read'],
  sourceIp: '203.0.113.7',
  sourceCountryCode: 'SG',
  statementType: 'SELECT',
  rowsReturned: 250000,
  actorBaselineRowsPerQuery: 500,
  applicableRegulators: ['DPDPA'],
  principalIdentifierHashes: [],
};

const STATUS_BADGE: Record<string, 'success' | 'warning' | 'glass'> = {
  active: 'success',
  published: 'success',
  paused: 'warning',
  dry_run: 'warning',
  draft: 'glass',
  pending_approval: 'warning',
  deprecated: 'glass',
};

export function DetectionRules() {
  const [rules, setRules] = useState<DetectionRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<DetectionRule | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);

  // Synchronous rule evaluator (T1.2 re-aligned 2026-05-29).
  // POSTs {finding?, event?, context?} to /v1/detection/evaluate. The
  // backend runs the full published rule set + UEBA + policy-graph votes
  // (Aerospace Triple Redundancy) and returns the aggregated decision.
  const [evalInput, setEvalInput] = useState(
    JSON.stringify(EVAL_SAMPLE, null, 2),
  );
  const [evaluating, setEvaluating] = useState(false);
  const [evalResult, setEvalResult] = useState<EvaluateRuleResult | null>(null);
  const [evalError, setEvalError] = useState<string | null>(null);

  async function handleEvaluate(): Promise<void> {
    setEvaluating(true);
    setEvalResult(null);
    setEvalError(null);
    try {
      const input = JSON.parse(evalInput) as EvaluateRuleInput;
      const result = await evaluateDetectionRule(input);
      setEvalResult(result);
    } catch (e) {
      setEvalError(e instanceof Error ? e.message : 'evaluation failed');
    } finally {
      setEvaluating(false);
    }
  }

  // Enforcement simulator (T1.2 re-aligned 2026-05-29).
  // POST /v1/detection/enforcement/run simulates a SINGLE database query
  // through the full enforcement pipeline (geofence → mass-export pre-cog
  // → consent firewall → provenance → section-9) and returns the strictest
  // decision across engines, with per-engine reasons + the synthetic SQL
  // error returned to the caller when the decision is `block`.
  const [enforceInput, setEnforceInput] = useState(
    JSON.stringify(ENFORCE_SAMPLE, null, 2),
  );
  const [enforceBusy, setEnforceBusy] = useState(false);
  const [enforceResult, setEnforceResult] = useState<RunEnforcementResult | null>(null);
  const [enforceError, setEnforceError] = useState<string | null>(null);

  async function handleRunEnforcement(): Promise<void> {
    setEnforceBusy(true);
    setEnforceResult(null);
    setEnforceError(null);
    try {
      const input = JSON.parse(enforceInput) as RunEnforcementInput;
      const r = await runDetectionEnforcement(input);
      setEnforceResult(r);
    } catch (e) {
      setEnforceError(e instanceof Error ? `Enforcement failed: ${e.message}` : 'Enforcement failed');
    } finally {
      setEnforceBusy(false);
    }
  }

  // Synthetic-error preview (T1.2 - surfaces the previously-dead
  // /enforcement/synthetic-error endpoint). Given a block Request ID +
  // a DB vendor, render the exact vendor-native SQL error the caller
  // would receive - the same string that carries the Request ID across
  // the decision record, audit chain, and SOC SIEM ticket.
  const [synReqId, setSynReqId] = useState('BLK-DEMO0001');
  const [synVendor, setSynVendor] = useState<DbVendor>('postgres');
  const [synTable, setSynTable] = useState('aadhaar_vault');
  const [synBusy, setSynBusy] = useState(false);
  const [synResult, setSynResult] = useState<SyntheticErrorResult | null>(null);
  const [synError, setSynError] = useState<string | null>(null);

  async function handleSyntheticError(): Promise<void> {
    setSynBusy(true);
    setSynResult(null);
    setSynError(null);
    try {
      const r = await postSyntheticError({
        requestId: synReqId.trim(),
        vendor: synVendor,
        ...(synTable.trim() ? { tableName: synTable.trim() } : {}),
      });
      setSynResult(r);
    } catch (e) {
      setSynError(e instanceof Error ? e.message : 'synthetic-error failed');
    } finally {
      setSynBusy(false);
    }
  }

  const refresh = (signal?: AbortSignal) =>
    listDetectionRules()
      .then((r) => !signal?.aborted && setRules(r.rules))
      .catch((err) => !signal?.aborted && setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => !signal?.aborted && setLoading(false));

  useEffect(() => {
    const ctrl = new AbortController();
    refresh(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  const counts = useMemo(() => ({
    active:   rules.filter((r) => r.status === 'active' || r.status === 'published').length,
    dryRun:   rules.filter((r) => r.status === 'dry_run').length,
    draft:    rules.filter((r) => r.status === 'draft').length,
    builtIn:  rules.filter((r) => r.builtIn === true).length,
    tenant:   rules.filter((r) => r.builtIn !== true).length,
  }), [rules]);

  async function onPatchStatus(rule: DetectionRule, status: 'active' | 'paused' | 'deprecated') {
    setPendingId(rule.id);
    try {
      await patchDetectionRuleStatus(rule.id, status);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'patch failed');
    } finally {
      setPendingId(null);
    }
  }

  async function onPublish(rule: DetectionRule) {
    setPendingId(rule.id);
    try {
      await publishDetectionRule(rule.id);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'publish failed');
    } finally {
      setPendingId(null);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Rules"
        subtitle="OPA Rego rules. Built-in rules cover the canonical India BFSI / DPDPA cases. Tenant rules use predict-before-publish dry-run before going active."
        badges={[
          { label: `${counts.builtIn} built-in`, variant: 'glass' },
          { label: `${counts.tenant} tenant`, variant: 'info' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: `${rules.length} total`, variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" onClick={() => setModalOpen(true)}>
            <Plus className="h-4 w-4" /> New rule
          </Button>
        }
      />

      {loading && (
        <LoadingState caption="Loading rules…" />
      )}
      {error && !loading && (
        <ErrorState
          title="Failed to load rules"
          detail={error}
          onRetry={() => { setError(null); setLoading(true); refresh(); }}
        />
      )}

      {!loading && (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-2"><CardTitle className="text-sm">Active</CardTitle></CardHeader>
              <div className="text-3xl font-display tabular">{counts.active}</div>
              <div className="text-xs text-muted-foreground">{counts.builtIn} built-in · {counts.tenant} tenant</div>
            </GlassCard>
            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-2"><CardTitle className="text-sm">Dry-run</CardTitle></CardHeader>
              <div className="text-3xl font-display tabular">{counts.dryRun}</div>
              <div className="text-xs text-muted-foreground">Predict-before-publish active</div>
            </GlassCard>
            <GlassCard className="p-5">
              <CardHeader className="p-0 mb-2"><CardTitle className="text-sm">Drafts</CardTitle></CardHeader>
              <div className="text-3xl font-display tabular">{counts.draft}</div>
              <div className="text-xs text-muted-foreground">Pending Legal Reviewer approval</div>
            </GlassCard>
          </div>

          {rules.length === 0 ? (
            <GlassCard className="p-8 text-center">
              <ScrollText className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <CardTitle className="text-base">No rules yet</CardTitle>
              <CardDescription className="text-sm mt-1">Click "New rule" to author your first Rego policy.</CardDescription>
            </GlassCard>
          ) : (
            <GlassCard className="p-0 overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/40">
                <div className="col-span-5">Rule</div>
                <div className="col-span-1">Severity</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Source</div>
                <div className="col-span-2 text-right">Actions</div>
              </div>
              {rules.map((r, i) => (
                <motion.div
                  key={r.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.025, 0.3) }}
                  className="grid grid-cols-12 items-center px-4 py-3 text-sm border-b border-border/30 hover:bg-muted/40 transition-colors"
                >
                  <div className="col-span-5 min-w-0">
                    <div className="flex items-center gap-2">
                      <ScrollText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <code className="font-mono text-xs truncate">{r.name}</code>
                    </div>
                    {r.description && (
                      <div className="text-[10px] text-muted-foreground mt-0.5 truncate ml-5.5">{r.description}</div>
                    )}
                  </div>
                  <div className="col-span-1">
                    <Badge variant={r.severity as 'critical' | 'high' | 'medium' | 'low'} className="text-[9px]">{r.severity}</Badge>
                  </div>
                  <div className="col-span-2">
                    <Badge variant={STATUS_BADGE[r.status] ?? 'glass'} className="text-[9px] capitalize">
                      {r.status === 'active' && <CheckCircle2 className="h-2.5 w-2.5 mr-1" />}
                      {r.status === 'paused' && <AlertTriangle className="h-2.5 w-2.5 mr-1" />}
                      {r.status.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <div className="col-span-2 text-xs text-muted-foreground">
                    {r.builtIn ? 'Built-in' : 'Tenant'}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-7" onClick={() => setSelected(r)} title="Inspect rule body">
                      <Eye className="h-3 w-3" />
                    </Button>
                    {r.status === 'draft' || r.status === 'dry_run' || r.status === 'pending_approval' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7"
                        onClick={() => onPublish(r)}
                        disabled={pendingId === r.id}
                        title="Publish (approver path)"
                      >
                        {pendingId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                      </Button>
                    ) : r.status === 'active' || r.status === 'published' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7"
                        onClick={() => onPatchStatus(r, 'paused')}
                        disabled={pendingId === r.id}
                        title="Pause this rule"
                      >
                        {pendingId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Pause'}
                      </Button>
                    ) : null}
                  </div>
                </motion.div>
              ))}
            </GlassCard>
          )}
        </>
      )}

      {/* Inspect drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex justify-end" onClick={() => setSelected(null)}>
          <div className="h-full w-full max-w-[640px] glass-strong border-l border-border/40 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 z-10 flex items-center gap-2 px-5 py-3 border-b border-border/40 glass-strong">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rule</div>
                <div className="text-sm font-mono truncate">{selected.name}</div>
              </div>
              <Button variant="glass" size="sm" onClick={() => setSelected(null)}><X className="h-4 w-4" /></Button>
            </div>
            <div className="p-5 space-y-4 text-sm">
              {selected.description && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Description</div>
                  <p>{selected.description}</p>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Metadata</div>
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                  <Badge variant={selected.severity as 'critical' | 'high' | 'medium' | 'low'}>{selected.severity}</Badge>
                  <Badge variant={STATUS_BADGE[selected.status] ?? 'glass'} className="capitalize">{selected.status.replace(/_/g, ' ')}</Badge>
                  <Badge variant="glass" className="capitalize">{selected.builtIn ? 'Built-in' : 'Tenant'}</Badge>
                  <Badge variant="glass" className="capitalize">{selected.ruleType.replace(/_/g, ' ')}</Badge>
                </div>
              </div>
              {selected.regoSource && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Rego source</div>
                  <pre className="text-[11px] glass-lite rounded-md p-3 overflow-x-auto font-mono leading-relaxed">{selected.regoSource}</pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DDR testers - rule evaluator + enforcement simulator + synthetic error */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <FlaskConical className="h-4 w-4 text-primary" /> Synchronous rule evaluator
            </CardTitle>
            <CardDescription>
              Test a <code className="font-mono text-[10px]">{'{finding?, event?, context?}'}</code> input
              against the active rule set + UEBA + policy-graph votes without writing a finding.
              Wraps <code className="font-mono text-[10px]">POST /v1/detection/evaluate</code>.
            </CardDescription>
          </CardHeader>
          <div className="space-y-2">
            <textarea
              value={evalInput}
              onChange={(e) => setEvalInput(e.target.value)}
              rows={11}
              spellCheck={false}
              className="glass-lite w-full px-3 py-2 rounded-md text-[11px] font-mono border border-border/40 focus:outline-none focus:border-primary"
            />
            <div className="flex justify-end">
              <Button size="sm" onClick={() => void handleEvaluate()} disabled={evaluating}>
                {evaluating ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <FlaskConical className="h-3 w-3 mr-1.5" />}
                {evaluating ? 'Evaluating…' : 'Evaluate'}
              </Button>
            </div>
            {evalError && (
              <div className="glass-lite rounded-md p-2 text-[11px] text-destructive border border-destructive/40">
                {evalError}
              </div>
            )}
            {evalResult && (
              <div className="glass-lite rounded-md p-3 text-xs space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant={
                      evalResult.redundancy.finalDecision === 'block' || evalResult.redundancy.finalDecision === 'quarantine'
                        ? 'critical'
                        : evalResult.redundancy.finalDecision === 'step_up' || evalResult.redundancy.finalDecision === 'break_glass'
                          ? 'warning'
                          : evalResult.redundancy.finalDecision === 'mask' || evalResult.redundancy.finalDecision === 'alert'
                            ? 'info'
                            : 'success'
                    }
                  >
                    {evalResult.redundancy.finalDecision}
                  </Badge>
                  {evalResult.redundancy.degraded && (
                    <Badge variant="warning" className="text-[9px]">degraded</Badge>
                  )}
                  <code className="text-[10px] text-muted-foreground">{evalResult.requestId}</code>
                </div>
                <p className="text-[11px] text-muted-foreground">{evalResult.redundancy.explanation}</p>
                {evalResult.engineResult.matchedRules.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {evalResult.engineResult.matchedRules.map((r) => (
                      <code key={r.ruleName} className="text-[10px] glass-lite px-1.5 py-0.5 rounded">
                        {r.ruleName}
                      </code>
                    ))}
                  </div>
                )}
                {evalResult.redundancy.votes.length > 0 && (
                  <div className="pt-1 border-t border-border/30 space-y-0.5">
                    {evalResult.redundancy.votes.map((v) => (
                      <div key={v.engine} className="flex justify-between text-[10px]">
                        <span className="text-muted-foreground">{v.engine}</span>
                        <span className="tabular">
                          {v.decision} · {(v.confidence * 100).toFixed(0)}%
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <Zap className="h-4 w-4 text-warning" /> Enforcement simulator (single query)
            </CardTitle>
            <CardDescription>
              Simulate one DB query through the full pipeline - geofence, mass-export pre-cog,
              consent firewall, provenance, section-9. Returns the strictest decision + the
              synthetic SQL error on block. Wraps <code className="font-mono text-[10px]">POST /enforcement/run</code>.
            </CardDescription>
          </CardHeader>
          <div className="space-y-2">
            <textarea
              value={enforceInput}
              onChange={(e) => setEnforceInput(e.target.value)}
              rows={11}
              spellCheck={false}
              className="glass-lite w-full px-3 py-2 rounded-md text-[11px] font-mono border border-border/40 focus:outline-none focus:border-primary"
            />
            <div className="flex justify-end">
              <Button size="sm" variant="default" onClick={() => void handleRunEnforcement()} disabled={enforceBusy}>
                {enforceBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Zap className="h-3 w-3 mr-1.5" />}
                {enforceBusy ? 'Running…' : 'Run pipeline'}
              </Button>
            </div>
            {enforceError && (
              <div className="glass-lite rounded-md p-2 text-[11px] text-destructive border border-destructive/40">
                {enforceError}
              </div>
            )}
            {enforceResult && (
              <div className="glass-lite rounded-md p-3 text-xs space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge
                    variant={
                      enforceResult.decision === 'block'
                        ? 'critical'
                        : enforceResult.decision === 'step_up'
                          ? 'warning'
                          : enforceResult.decision === 'mask'
                            ? 'info'
                            : 'success'
                    }
                  >
                    {enforceResult.decision}
                  </Badge>
                  <code className="text-[10px] text-muted-foreground">{enforceResult.requestId}</code>
                  {enforceResult.massExportMultiplier !== undefined && (
                    <span className="text-[10px] text-muted-foreground tabular">
                      mass-export ×{enforceResult.massExportMultiplier.toFixed(1)}
                    </span>
                  )}
                </div>
                {enforceResult.reasons.length > 0 && (
                  <div className="space-y-0.5">
                    {enforceResult.reasons.map((r, i) => (
                      <div key={`${r.engine}-${i}`} className="flex justify-between gap-2 text-[10px]">
                        <span className="text-muted-foreground shrink-0">{r.engine}</span>
                        <span className="text-right">{r.reason}</span>
                      </div>
                    ))}
                  </div>
                )}
                {enforceResult.syntheticError && (
                  <pre className="text-[10px] glass-lite rounded p-2 overflow-x-auto font-mono text-destructive">
                    {enforceResult.syntheticError.code} · {enforceResult.syntheticError.message}
                  </pre>
                )}
              </div>
            )}
          </div>
        </GlassCard>
      </div>

      {/* Synthetic-error preview (surfaces /enforcement/synthetic-error) */}
      <GlassCard className="p-5 mt-4">
        <CardHeader className="p-0 mb-3">
          <CardTitle className="flex items-center gap-2 text-sm">
            <Bug className="h-4 w-4 text-destructive" /> Synthetic SQL-error preview
          </CardTitle>
          <CardDescription>
            Render the exact vendor-native error a blocked caller receives. The Request ID is
            embedded in the message so it correlates across the decision record, audit chain,
            and SOC SIEM ticket. Wraps <code className="font-mono text-[10px]">POST /enforcement/synthetic-error</code>.
          </CardDescription>
        </CardHeader>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Request ID</label>
            <input
              type="text"
              value={synReqId}
              onChange={(e) => setSynReqId(e.target.value)}
              placeholder="BLK-XXXXXXXX"
              className="glass-lite block w-44 px-3 py-1.5 mt-1 rounded-md text-xs font-mono border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Vendor</label>
            <select
              value={synVendor}
              onChange={(e) => setSynVendor(e.target.value as DbVendor)}
              className="glass-lite block w-32 px-3 py-1.5 mt-1 rounded-md text-xs bg-transparent border border-border/40 focus:outline-none focus:border-primary"
            >
              <option value="postgres">postgres</option>
              <option value="mysql">mysql</option>
              <option value="mssql">mssql</option>
              <option value="oracle">oracle</option>
              <option value="mongodb">mongodb</option>
            </select>
          </div>
          <div className="flex-1 min-w-[10rem]">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Table (optional)</label>
            <input
              type="text"
              value={synTable}
              onChange={(e) => setSynTable(e.target.value)}
              className="glass-lite block w-full px-3 py-1.5 mt-1 rounded-md text-xs font-mono border border-border/40 focus:outline-none focus:border-primary"
            />
          </div>
          <Button size="sm" onClick={() => void handleSyntheticError()} disabled={synBusy}>
            {synBusy ? <Loader2 className="h-3 w-3 animate-spin mr-1.5" /> : <Bug className="h-3 w-3 mr-1.5" />}
            {synBusy ? 'Generating…' : 'Preview'}
          </Button>
        </div>
        {synError && (
          <div className="glass-lite rounded-md p-2 mt-3 text-[11px] text-destructive border border-destructive/40">
            {synError}
          </div>
        )}
        {synResult && (
          <pre className="text-[11px] glass-lite rounded-md p-3 mt-3 overflow-x-auto font-mono leading-relaxed">
            <span className="text-muted-foreground">{synResult.severity} [{synResult.code} / {synResult.errorNumber}]</span>
            {'\n'}{synResult.message}
          </pre>
        )}
      </GlassCard>

      {/* New-rule modal */}
      {modalOpen && (
        <NewRuleModal
          onClose={() => setModalOpen(false)}
          onCreated={async () => { setModalOpen(false); await refresh(); }}
        />
      )}
    </PageContainer>
  );
}

interface NewRuleModalProps {
  onClose: () => void;
  onCreated: () => void;
}

function NewRuleModal({ onClose, onCreated }: NewRuleModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<'critical' | 'high' | 'medium' | 'low'>('medium');
  const [rego, setRego] = useState('package foctta.detection.custom\ndefault decision := "allow"\n');
  const [busy, setBusy] = useState<'idle' | 'creating' | 'dry-running'>('idle');
  const [dryRunResult, setDryRunResult] = useState<{ wouldMatchCount: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function onDryRun() {
    setBusy('dry-running');
    setErr(null);
    try {
      const r = await dryRunDetectionRule({ ruleBody: { rego } });
      setDryRunResult({ wouldMatchCount: r.wouldMatchCount });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'dry-run failed');
    } finally {
      setBusy('idle');
    }
  }

  async function onSaveDraft() {
    if (!name) return;
    setBusy('creating');
    setErr(null);
    try {
      await createDetectionRule({
        name,
        description,
        ruleType: 'combined',
        severity,
        regoSource: rego,
      });
      onCreated();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'create failed');
      setBusy('idle');
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-background/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass-strong border border-border/40 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 flex items-center justify-between gap-2 px-5 py-3 border-b border-border/40 glass-strong">
          <CardTitle className="text-base">New rule (draft)</CardTitle>
          <Button variant="glass" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <div className="p-5 space-y-3 text-sm">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Name</label>
            <input
              className="glass-lite rounded-md w-full px-3 py-2 mt-1 bg-transparent border border-border/40 font-mono text-sm"
              placeholder="tenant_custom_aadhaar_in_logs"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Description</label>
            <input
              className="glass-lite rounded-md w-full px-3 py-2 mt-1 bg-transparent border border-border/40 text-sm"
              placeholder="What this rule detects + what it does"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Severity</label>
            <select
              className="glass-lite rounded-md w-full px-3 py-2 mt-1 bg-transparent border border-border/40 text-sm"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as 'critical' | 'high' | 'medium' | 'low')}
            >
              <option value="critical">critical</option>
              <option value="high">high</option>
              <option value="medium">medium</option>
              <option value="low">low</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Rego body</label>
            <textarea
              className="glass-lite rounded-md w-full px-3 py-2 mt-1 bg-transparent border border-border/40 font-mono text-[11px] leading-relaxed"
              rows={10}
              value={rego}
              onChange={(e) => setRego(e.target.value)}
            />
          </div>
          {dryRunResult && (
            <div className="glass-lite border border-info/40 rounded-md p-3 text-xs">
              Dry-run: rule would match <strong>{dryRunResult.wouldMatchCount}</strong> recent events. Save draft to keep iterating.
            </div>
          )}
          {err && (
            <div className="glass-lite border border-sev-high/40 rounded-md p-3 text-xs flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-sev-high shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          )}
        </div>
        <div className="sticky bottom-0 flex items-center justify-end gap-2 px-5 py-3 border-t border-border/40 glass-strong">
          <Button variant="glass" size="sm" onClick={onDryRun} disabled={busy !== 'idle'}>
            {busy === 'dry-running' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Dry-run
          </Button>
          <Button size="sm" onClick={onSaveDraft} disabled={busy !== 'idle' || !name}>
            {busy === 'creating' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Save draft
          </Button>
        </div>
      </div>
    </div>
  );
}
